/**
 * PaymentsAdapter for live mode.
 *  - Server (Edge Functions): `createServerPayments` writes `payments` rows and talks to the OWNER's gateway through a GatewayProvider
 *    (Grow first, PayPlus second; credentials come from Supabase Vault, never from this repo). Without a configured gateway the card
 *    method must stay off for that business (settings.paymentMethods.card = false): Bit/PayBox are owner payment links confirmed by hand.
 *  - Browser: `createClientPayments` only reads (RLS lets owners select their payments); every write happens on the server.
 */
import { MINUTE, toIso, toMs } from '../../domain/time';
import { newId, type Payment } from '../../domain/types';
import type { Clock, PaymentsAdapter, StorageAdapter, Unsubscribe } from '../types';

export interface GatewayPage { gatewayRef: string; checkoutUrl: string }
export interface GatewayProvider {
  readonly name: Payment['gateway'];
  createPaymentPage(input: { businessId: string; paymentId: string; appointmentId: string; amountAgorot: number; expiresAt: string; returnUrl: string }): Promise<GatewayPage>;
  refund(input: { businessId: string; gatewayRef: string; amountAgorot: number }): Promise<{ ok: boolean }>;
}

export function createServerPayments(deps: { storage: StorageAdapter; clock: Clock; gateway: GatewayProvider | undefined; returnUrl: (paymentId: string) => string }): PaymentsAdapter {
  const { storage, clock, gateway } = deps;
  const repo = storage.repo('payments');
  const listeners = new Set<(p: Payment) => void>();
  const announce = (p: Payment): void => { for (const cb of [...listeners]) cb(p); };
  async function transition(id: string, change: Partial<Payment>, from: Payment['status'][]): Promise<Payment | undefined> {
    const cur = await repo.get(id);
    if (!cur || !from.includes(cur.status)) return cur;
    const next: Payment = { ...cur, ...change, updatedAt: clock.nowIso() };
    await repo.put(next);
    announce(next);
    return next;
  }
  return {
    label: gateway ? `Payment gateway (${gateway.name})` : 'Payment gateway (not configured)',
    async createDeposit(req) {
      if (!gateway) throw new Error('gateway_not_configured');
      const now = clock.nowIso();
      const id = newId(clock.now());
      const expiresAt = toIso(clock.now() + req.holdMinutes * MINUTE);
      const page = await gateway.createPaymentPage({ businessId: req.businessId, paymentId: id, appointmentId: req.appointmentId, amountAgorot: req.amountAgorot, expiresAt, returnUrl: deps.returnUrl(id) });
      const p: Payment = { id, businessId: req.businessId, createdAt: now, updatedAt: now, appointmentId: req.appointmentId, amountAgorot: req.amountAgorot, refundedAgorot: 0, status: 'pending', gateway: gateway.name as 'mock', expiresAt };
      await repo.put({ ...p, gatewayRef: page.gatewayRef } as Payment);
      return { paymentId: id, checkoutUrl: page.checkoutUrl };
    },
    getStatus: (id) => repo.get(id),
    async refund(id, amount) {
      const cur = await repo.get(id);
      if (!cur) return { ok: false, reason: 'not_found' };
      if (cur.status !== 'paid' && cur.status !== 'partially_refunded') return { ok: false, reason: 'not_paid' };
      const refundable = cur.amountAgorot - cur.refundedAgorot;
      const value = amount ?? refundable;
      if (value > refundable || value <= 0) return { ok: false, reason: 'exceeds_paid' };
      const ref = (cur as Payment & { gatewayRef?: string }).gatewayRef;   // stored by createDeposit (mapped to payments.gateway_ref)
      if (gateway && ref) await gateway.refund({ businessId: cur.businessId, gatewayRef: ref, amountAgorot: value });
      const refunded = cur.refundedAgorot + value;
      const next: Payment = { ...cur, refundedAgorot: refunded, status: refunded >= cur.amountAgorot ? 'refunded' : 'partially_refunded', updatedAt: clock.nowIso() };
      await repo.put(next);
      announce(next);
      return { ok: true, payment: next };
    },
    /** Called by the gateway webhook handler (Phase 2) and by the failure/timeout paths of the use cases. */
    async complete(id, outcome) {
      if (outcome === 'success') {
        const cur = await repo.get(id);
        if (cur && cur.status === 'pending' && toMs(cur.expiresAt) <= clock.now()) return transition(id, { status: 'expired' }, ['pending']);
        return transition(id, { status: 'paid', paidAt: clock.nowIso() }, ['pending']);
      }
      return transition(id, { status: outcome === 'failure' ? 'failed' : 'expired' }, ['pending']);
    },
    async sweepExpired() {
      const now = clock.now();
      const out: Payment[] = [];
      for (const p of await repo.list({ where: { status: 'pending' }, filter: (x) => toMs(x.expiresAt) <= now })) {
        const next = await transition(p.id, { status: 'expired' }, ['pending']);
        if (next) out.push(next);
      }
      return out;
    },
    onStatus(cb): Unsubscribe { listeners.add(cb); return () => void listeners.delete(cb); },
  };
}

const serverOnly = (): never => { throw new Error('server_only: payments are created and settled by Edge Functions'); };
export function createClientPayments(deps: { storage: StorageAdapter }): PaymentsAdapter {
  const repo = deps.storage.repo('payments');
  return {
    label: 'Payment gateway (server side)',
    createDeposit: serverOnly, refund: serverOnly, complete: serverOnly,
    getStatus: (id) => repo.get(id),
    sweepExpired: async () => [],
    onStatus(cb): Unsubscribe {
      return deps.storage.subscribe('payments', (e) => { if (e.id && e.type !== 'delete') void repo.get(e.id).then((p) => { if (p) cb(p); }); });
    },
  };
}
